import { useEffect, useRef, useState } from "react";
import { toast } from "sonner";
import { Loader2, Wallet, X } from "lucide-react";
import { supabase } from "@/lib/supabaseClient";
import {
  buildCollectPaymentRpcArgs,
  collectPaymentNeedsReference,
} from "@/lib/collectPaymentForm";

// =====================================================================
// CollectPaymentDialog — universal "collect a payment" modal.
// Used both mid-treatment (Accepted plan, intermediate payment) AND
// for AR plans (status=Completed with balance > 0).
//
// Calls public.collect_payment RPC. On success, fires onCollected.
// =====================================================================

const PAYMENT_METHODS = [
  { id: "cash",          label: "Cash" },
  { id: "gcash",         label: "GCash" },
  { id: "maya",          label: "Maya" },
  { id: "bank_transfer", label: "Bank transfer" },
  { id: "card",          label: "Card" },
  { id: "hmo",           label: "HMO" },
  { id: "check",         label: "Check" },
];

function formatPHP(centavos) {
  if (!Number.isFinite(centavos)) return "—";
  return `₱${Math.round(centavos / 100).toLocaleString()}`;
}

export default function CollectPaymentDialog({
  open,
  onOpenChange,
  planId,
  balanceCentavos = 0,
  label = "Collect Payment",
  balanceLabel = "Outstanding",
  successMessage,
  paymentMethods = PAYMENT_METHODS,
  onCollected,
}) {
  const [amount,  setAmount]  = useState(0);
  const [method,  setMethod]  = useState("cash");
  const [notes,   setNotes]   = useState("");
  const [referenceNumber, setReferenceNumber] = useState("");
  const [submitting, setSubmitting] = useState(false);
  const idempotencyKeyRef = useRef(null);

  useEffect(() => {
    if (!open) return;
    idempotencyKeyRef.current =
      globalThis.crypto?.randomUUID?.() ||
      `collect-payment-${Date.now()}-${Math.random().toString(36).slice(2)}`;
    setAmount(balanceCentavos);
    setMethod("cash");
    setNotes("");
    setReferenceNumber("");
    setSubmitting(false);
  }, [open, balanceCentavos]);

  if (!open) return null;

  const amtValid = Number.isFinite(Number(amount)) && Number(amount) > 0 && Number(amount) <= balanceCentavos;
  const balAfter = Math.max(balanceCentavos - (Number(amount) || 0), 0);
  const needsRef = collectPaymentNeedsReference(method, amount);
  const refValid = !needsRef || referenceNumber.trim().length > 0;

  async function handleSubmit() {
    if (!amtValid || !method || !refValid) return;
    setSubmitting(true);
    const { data, error } = await supabase.rpc("collect_payment", buildCollectPaymentRpcArgs({
      planId,
      amountCentavos: Number(amount),
      method,
      notes,
      referenceNumber,
      idempotencyKey: idempotencyKeyRef.current,
    }));
    setSubmitting(false);
    if (error) {
      toast.error(error.message ?? "Could not collect payment.");
      return;
    }
    toast.success(successMessage || `Collected ${formatPHP(amount)}`);
    onCollected?.({
      ...(data || {}),
      payment: {
        amount_centavos: Number(amount),
        method,
        notes: notes?.trim() || null,
        reference_number: referenceNumber.trim() || null,
      },
    });
    onOpenChange?.(false);
  }

  return (
    <div className="fixed inset-0 z-50 flex items-center justify-center bg-slate-900/40 px-4"
         role="dialog" aria-modal="true" data-testid="collect-payment-dialog">
      <div className="bg-white rounded-2xl shadow-xl w-full max-w-md">
        <div className="flex items-start justify-between gap-4 px-5 py-4 border-b border-slate-100">
          <div>
            <h2 className="text-base font-semibold text-slate-900 inline-flex items-center gap-2">
              <Wallet className="w-4 h-4 text-emerald-600" /> {label}
            </h2>
            <p className="text-xs text-slate-500 mt-1">
              {balanceLabel} <span className="font-semibold text-slate-900">{formatPHP(balanceCentavos)}</span>
            </p>
          </div>
          <button type="button" onClick={() => onOpenChange?.(false)}
                  className="text-slate-400 hover:text-slate-700"
                  aria-label="Close" data-testid="collect-payment-close">
            <X className="w-4 h-4" />
          </button>
        </div>

        <div className="p-5 space-y-4">
          <label className="block">
            <span className="text-xs text-slate-600">Amount (₱, in pesos)</span>
            <input
              type="number" min="0" max={Math.round(balanceCentavos / 100)}
              value={Math.round((amount || 0) / 100)}
              onChange={(e) => setAmount(Math.max(0, Math.round(Number(e.target.value || 0))) * 100)}
              data-testid="collect-payment-amount"
              disabled={submitting}
              className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm tabular-nums focus:outline-none focus:border-emerald-500"
            />
          </label>

          <label className="block">
            <span className="text-xs text-slate-600">Method</span>
            <select value={method} onChange={(e) => setMethod(e.target.value)}
                    data-testid="collect-payment-method" disabled={submitting}
                    className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500">
              {paymentMethods.map((m) => <option key={m.id} value={m.id}>{m.label}</option>)}
            </select>
          </label>

          {needsRef ? (
            <label className="block">
              <span className="text-xs text-slate-600">Reference / transaction #</span>
              <input
                type="text"
                value={referenceNumber}
                onChange={(e) => setReferenceNumber(e.target.value)}
                placeholder="Required for this payment method"
                data-testid="collect-payment-reference"
                disabled={submitting}
                className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500"
              />
            </label>
          ) : null}

          <label className="block">
            <span className="text-xs text-slate-600">Notes (optional)</span>
            <input type="text" value={notes} onChange={(e) => setNotes(e.target.value)}
                   placeholder="e.g. partial payment, post-dated check #1234"
                   data-testid="collect-payment-notes" disabled={submitting}
                   className="mt-1 w-full px-3 py-2 border border-slate-300 rounded-lg text-sm focus:outline-none focus:border-emerald-500" />
          </label>

          <div className="bg-slate-50 rounded-lg px-3 py-2 flex items-center justify-between text-xs">
            <span className="text-slate-600">Balance after</span>
            <span className="text-sm font-semibold text-slate-900 tabular-nums" data-testid="collect-payment-balance-after">
              {formatPHP(balAfter)}
            </span>
          </div>
        </div>

        <div className="flex items-center justify-between gap-3 px-5 py-4 border-t border-slate-100">
          <button type="button" onClick={() => onOpenChange?.(false)} disabled={submitting}
                  data-testid="collect-payment-cancel"
                  className="px-3 py-2 text-xs font-semibold rounded-lg border border-slate-200 text-slate-700 bg-white hover:border-slate-400">
            Cancel
          </button>
          <button type="button" onClick={handleSubmit} disabled={!amtValid || !refValid || submitting}
                  data-testid="collect-payment-submit"
                  className="px-4 py-2 text-sm font-semibold rounded-lg bg-emerald-600 text-white hover:bg-emerald-700 disabled:opacity-50 inline-flex items-center gap-2">
            {submitting ? <Loader2 className="w-4 h-4 animate-spin" /> : <Wallet className="w-4 h-4" />}
            Collect {formatPHP(amount)}
          </button>
        </div>
      </div>
    </div>
  );
}
